/**
 * Converts Amazon States Language (ASL) to React Flow graph format.
 * Parses ASL deterministically - no AI required.
 * Adds Start and End circle nodes for a complete workflow diagram.
 *
 * Final node `{ x, y }` come from Dagre with `rankdir: 'TB'` (top→bottom).
 * React Flow itself has no orientation prop — positions are explicit.
 */

import dagre from 'dagre';

// Layout — generous spacing; agent nodes use this exact width/height so columns never visually collide.
export const ASL_GRAPH_NODE_WIDTH = 180;
export const ASL_GRAPH_NODE_HEIGHT = 48;
const NODE_WIDTH = ASL_GRAPH_NODE_WIDTH;
const NODE_HEIGHT = ASL_GRAPH_NODE_HEIGHT;
const SPACING_X = 36;
const SPACING_Y = 80;
const START_NODE_ID = '__start__';
const END_NODE_ID = '__end__';

/**
 * Check if an object looks like valid ASL
 * @param {object} obj
 * @returns {boolean}
 */
export function isASL(obj) {
  if (!obj || typeof obj !== 'object') return false;
  const states = obj.States || obj.states;
  const startAt = obj.StartAt || obj.startAt;
  return !!states && typeof states === 'object' && !!startAt;
}

/** Get label from state (AgentConfig.logicalId or state name) */
function getStateLabel(s, name) {
  const agentConfig = s?.AgentConfig || s?.agentConfig;
  return agentConfig?.logicalId ?? agentConfig?.logical_id ?? name;
}

/** Find terminal state names in a branch/iterator (End:true, Succeed, Fail) */
function getBranchTerminals(branchStates, branchStartAt) {
  const terminals = [];
  const visited = new Set();
  const queue = [branchStartAt];
  while (queue.length) {
    const n = queue.shift();
    if (visited.has(n)) continue;
    visited.add(n);
    const s = branchStates[n];
    if (!s) continue;
    const type = s.Type || s.type;
    if (type === 'Succeed' || type === 'Fail' || s.End === true || s.end === true) {
      terminals.push(n);
      continue;
    }
    if (s.Next || s.next) queue.push(s.Next || s.next);
    if (type === 'Choice') {
      (s.Choices || s.choices || []).forEach((c) => c.Next && queue.push(c.Next));
      if (s.Default || s.default) queue.push(s.Default || s.default);
    }
  }
  return terminals;
}

function readNodeMeasuredSize(node) {
  const w = node.style?.width ?? NODE_WIDTH;
  const h = node.style?.height ?? NODE_HEIGHT;
  return {
    width: typeof w === 'number' ? w : NODE_WIDTH,
    height: typeof h === 'number' ? h : NODE_HEIGHT,
  };
}

/**
 * Lay out a React Flow graph top→bottom using Dagre (`rankdir: 'TB'`). Used
 * here AND inside `<GraphVisualization>` / `<MiniFlow>` because those
 * components also receive pre-baked graphs from the backend that carry
 * stale L→R positions. Re-running Dagre at render time guarantees TB.
 */
export function applyDagreTopBottom(nodes, edges, opts = {}) {
  const g = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  /**
   * Tight defaults so the **blocks dominate** the canvas, not the connectors:
   *   - `ranksep` is the vertical gap between rows (= visible edge length in TB).
   *   - `nodesep` is the horizontal gap between siblings (parallel branches).
   * Callers can override per surface (e.g. mini tile uses even tighter values).
   */
  g.setGraph({
    rankdir: 'TB',
    nodesep: opts.nodesep ?? 24,
    ranksep: opts.ranksep ?? 36,
    marginx: opts.marginx ?? 16,
    marginy: opts.marginy ?? 16,
    edgesep: opts.edgesep ?? 8,
    align: 'UL',
  });

  for (const node of nodes) {
    const { width, height } = readNodeMeasuredSize(node);
    g.setNode(node.id, { width, height });
  }
  for (const e of edges) {
    g.setEdge(e.source, e.target);
  }
  dagre.layout(g);

  return nodes.map((node) => {
    const d = g.node(node.id);
    if (!d) return node;
    const { width, height } = readNodeMeasuredSize(node);
    return {
      ...node,
      position: {
        x: d.x - width / 2,
        y: d.y - height / 2,
      },
    };
  });
}

/**
 * Parse ASL (object or JSON string) into React Flow nodes and edges
 * @param {object|string} asl - ASL definition
 * @returns {{ nodes: Array, edges: Array }}
 */
export function aslToReactFlow(asl) {
  if (!asl) return { nodes: [], edges: [] };

  let parsed = asl;
  if (typeof asl === 'string') {
    try {
      parsed = JSON.parse(asl);
    } catch {
      return { nodes: [], edges: [] };
    }
  }

  if (!isASL(parsed)) return { nodes: [], edges: [] };

  const states = parsed.States || parsed.states;
  const startAt = parsed.StartAt || parsed.startAt;
  const stateNames = Object.keys(states);

  // --- Phase 1: Expand Parallel/Map into flat graph ---
  const nodeMeta = new Map(); // id -> { label, type, comment }
  const expandedOutgoing = new Map(); // id -> [targetIds]
  const expandedIncoming = new Map(); // id -> [sourceIds] (for routing to Parallel entry)

  function addExpandedNode(id, meta) {
    nodeMeta.set(id, meta);
  }
  function addExpandedEdge(from, to) {
    if (!expandedOutgoing.has(from)) expandedOutgoing.set(from, []);
    expandedOutgoing.get(from).push(to);
  }

  const parallelOrMapStates = new Set();
  stateNames.forEach((name) => {
    const s = states[name];
    const type = s.Type || s.type || 'Pass';
    if (type === 'Parallel') {
      parallelOrMapStates.add(name);
      const branches = s.Branches || s.branches || [];
      const nextAfter = s.Next || s.next;
      const entryIds = [];
      const exitIds = [];
      branches.forEach((branch, i) => {
        const bStart = branch.StartAt || branch.startAt;
        const bStates = branch.States || branch.states || {};
        const bNames = Object.keys(bStates);
        bNames.forEach((bName) => {
          const bid = `${name}__b${i}__${bName}`;
          const bs = bStates[bName];
          const btype = bs.Type || bs.type || 'Pass';
          addExpandedNode(bid, {
            label: getStateLabel(bs, bName),
            type: btype,
            comment: bs.Comment || bs.comment || ''
          });
          if (bName === bStart) entryIds.push(bid);
          const terms = getBranchTerminals(bStates, bStart);
          if (terms.includes(bName)) exitIds.push(bid);
          if (bs.Next || bs.next) addExpandedEdge(bid, `${name}__b${i}__${bs.Next || bs.next}`);
          if (btype === 'Choice') {
            (bs.Choices || bs.choices || []).forEach((c) => {
              if (c.Next || c.next) addExpandedEdge(bid, `${name}__b${i}__${c.Next || c.next}`);
            });
            if (bs.Default || bs.default) addExpandedEdge(bid, `${name}__b${i}__${bs.Default || bs.default}`);
          }
        });
      });
      if (nextAfter) {
        exitIds.forEach((eid) => addExpandedEdge(eid, nextAfter));
      }
      if (!expandedIncoming.has(name)) expandedIncoming.set(name, { entryIds });
      else expandedIncoming.get(name).entryIds = entryIds;
    } else if (type === 'Map') {
      parallelOrMapStates.add(name);
      const it = s.Iterator || s.iterator;
      const nextAfter = s.Next || s.next;
      if (it) {
        const bStart = it.StartAt || it.startAt;
        const bStates = it.States || it.states || {};
        const bNames = Object.keys(bStates);
        const entryIds = [];
        const exitIds = [];
        bNames.forEach((bName) => {
          const bid = `${name}__map__${bName}`;
          const bs = bStates[bName];
          const btype = bs.Type || bs.type || 'Pass';
          addExpandedNode(bid, {
            label: getStateLabel(bs, bName),
            type: btype,
            comment: bs.Comment || bs.comment || ''
          });
          if (bName === bStart) entryIds.push(bid);
          const terms = getBranchTerminals(bStates, bStart);
          if (terms.includes(bName)) exitIds.push(bid);
          if (bs.Next || bs.next) addExpandedEdge(bid, `${name}__map__${bs.Next || bs.next}`);
          if (btype === 'Choice') {
            (bs.Choices || bs.choices || []).forEach((c) => {
              if (c.Next || c.next) addExpandedEdge(bid, `${name}__map__${c.Next || c.next}`);
            });
            if (bs.Default || bs.default) addExpandedEdge(bid, `${name}__map__${bs.Default || bs.default}`);
          }
        });
        if (nextAfter) {
          exitIds.forEach((eid) => addExpandedEdge(eid, nextAfter));
        }
        if (!expandedIncoming.has(name)) expandedIncoming.set(name, { entryIds });
        else expandedIncoming.get(name).entryIds = entryIds;
      }
    } else {
      addExpandedNode(name, {
        label: getStateLabel(s, name),
        type,
        comment: s.Comment || s.comment || ''
      });
      if (s.Next || s.next) {
        const next = s.Next || s.next;
        if (parallelOrMapStates.has(next)) {
          const inc = expandedIncoming.get(next);
          if (inc?.entryIds) inc.entryIds.forEach((eid) => addExpandedEdge(name, eid));
        } else {
          addExpandedEdge(name, next);
        }
      }
      if (type === 'Choice') {
        (s.Choices || s.choices || []).forEach((c) => {
          if (c.Next || c.next) {
            const t = c.Next || c.next;
            if (parallelOrMapStates.has(t)) {
              const inc = expandedIncoming.get(t);
              if (inc?.entryIds) inc.entryIds.forEach((eid) => addExpandedEdge(name, eid));
            } else addExpandedEdge(name, t);
          }
        });
        if (s.Default || s.default) {
          const t = s.Default || s.default;
          if (parallelOrMapStates.has(t)) {
            const inc = expandedIncoming.get(t);
            if (inc?.entryIds) inc.entryIds.forEach((eid) => addExpandedEdge(name, eid));
          } else addExpandedEdge(name, t);
        }
      }
      (s.Catch || s.catch || []).forEach((c) => {
        if (c.Next || c.next) addExpandedEdge(name, c.Next || c.next);
      });
    }
  });

  // Entry points for BFS
  const entryIds = parallelOrMapStates.has(startAt)
    ? (expandedIncoming.get(startAt)?.entryIds || [])
    : [startAt];

  // Terminal node ids (connect to End)
  const terminalIds = [];
  nodeMeta.forEach((_, id) => {
    const s = states[id];
    if (s) {
      const type = s.Type || s.type || 'Pass';
      if (type === 'Succeed' || type === 'Fail' || s?.End === true || s?.end === true) terminalIds.push(id);
    } else {
      const outs = expandedOutgoing.get(id) || [];
      if (outs.length === 0) terminalIds.push(id);
    }
  });
  stateNames.forEach((name) => {
    if (parallelOrMapStates.has(name)) return;
    const s = states[name];
    const type = s.Type || s.type || 'Pass';
    if (type === 'Succeed' || type === 'Fail') terminalIds.push(name);
    else if (s.End === true || s.end === true) terminalIds.push(name);
    else if (type !== 'Choice' && !(s.Next || s.next)) terminalIds.push(name);
  });

  const circleSize = 48;

  /** Placeholder positions — Dagre overrides them once we know the edges. */
  const stateNodes = [];
  nodeMeta.forEach((meta, id) => {
    stateNodes.push({
      id,
      type: 'agent',
      position: { x: 0, y: 0 },
      data: {
        label: meta.label,
        comment: typeof meta.comment === 'string' ? meta.comment : '',
        aslType: meta.type,
      },
      style: { width: NODE_WIDTH, height: NODE_HEIGHT },
    });
  });

  const startNode = {
    id: START_NODE_ID,
    type: 'startNode',
    position: { x: 0, y: 0 },
    data: { label: 'Start', aslType: 'start' },
    style: { width: circleSize, height: circleSize },
  };
  const endNode = {
    id: END_NODE_ID,
    type: 'endNode',
    position: { x: 0, y: 0 },
    data: { label: 'End', aslType: 'end' },
    style: { width: circleSize, height: circleSize },
  };
  const nodes = [startNode, ...stateNodes, endNode];

  const edgeSet = new Set();
  const edges = [];
  function addEdge(source, target, label) {
    const key = `${source}->${target}${label ? `:${label}` : ''}`;
    if (edgeSet.has(key)) return;
    edgeSet.add(key);
    edges.push({
      id: `e-${source}-${target}-${edges.length}`,
      source,
      target,
      ...(label && { label }),
    });
  }

  entryIds.forEach((eid) => addEdge(START_NODE_ID, eid));
  expandedOutgoing.forEach((targets, src) => {
    targets.forEach((tgt) => addEdge(src, tgt));
  });
  terminalIds.forEach((tid) => addEdge(tid, END_NODE_ID));

  const positionedNodes = applyDagreTopBottom(nodes, edges);
  return { nodes: positionedNodes, edges };
}
